import { Select } from '@mui/material';
import type { SelectProps as MuiSelectProps } from '@mui/material';
import { Controller } from 'react-hook-form';
import type { Control, FieldValues, FieldPath } from 'react-hook-form';

import { MenuItem } from '@liscio/ui';

import type { EmployeeStatusFilter } from 'custom-hooks/useEmployeeList';
import { useEmployeeList } from 'custom-hooks/useEmployeeList';

interface Owner {
  uuid: string;
  cpa_user_id?: string | number;
  id?: string | number;
  first_name: string;
  last_name: string;
}

type SelectOwnerDropdownProps<TFieldValues extends FieldValues = FieldValues> = MuiSelectProps & {
  control?: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  value?: string | undefined;
  statusFilter?: EmployeeStatusFilter;
  idFieldName?: 'uuid' | 'cpa_user_id' | 'id';
};

/**
 * This component is used to select an owner from a dropdown.
 */
export const SelectOwnerDropdown = <TFieldValues extends FieldValues = FieldValues>({
  control = undefined,
  name,
  value,
  statusFilter,
  idFieldName = 'uuid',
  onChange,
  sx,
  ...props
}: SelectOwnerDropdownProps<TFieldValues>) => {
  const { employeeList: ownersList } = useEmployeeList({
    enabled: true,
    statusFilter: statusFilter,
  });

  // Define MenuProps to position the dropdown below the select field
  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
    disableScrollLock: true,
  };

  // Normalize option value based on idFieldName and coerce to string
  const getOwnerValue = (owner: Owner): string =>
    String(owner[idFieldName] ?? owner.uuid ?? '');

  const renderMenuItems = () =>
    ownersList.map((owner) => (
      <MenuItem key={owner.uuid} value={getOwnerValue(owner)}>
        {owner.first_name} {owner.last_name}
      </MenuItem>
    ));

  if (control)
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select fullWidth {...props} MenuProps={menuProps} sx={sx} {...field}>
            {renderMenuItems()}
          </Select>
        )}
      />
    );

  return (
    <Select
      onChange={onChange}
      value={value?.toString() || ''}
      MenuProps={menuProps}
      sx={sx}
      {...props}
    >
      {renderMenuItems()}
    </Select>
  );
};
